import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Check, Sparkles } from 'lucide-react';
import { AVATARS, AVATAR_CATEGORIES, AvatarItem, getAvatarsByCategory } from '../data/avatars';

interface AvatarLibraryProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (avatarUrl: string) => void;
    currentAvatar?: string;
}

const AvatarLibrary: React.FC<AvatarLibraryProps> = ({ isOpen, onClose, onSelect, currentAvatar }) => {
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [hoveredAvatar, setHoveredAvatar] = useState<string | null>(null);

    const filteredAvatars = useMemo(() => {
        let avatars = getAvatarsByCategory(selectedCategory);
        if (searchQuery) {
            avatars = avatars.filter(a =>
                a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                a.category.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }
        return avatars;
    }, [selectedCategory, searchQuery]);

    const handleSelect = (avatar: AvatarItem) => {
        onSelect(avatar.url);
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-xl flex flex-col"
                >
                    {/* Header */}
                    <header className="flex items-center justify-between p-4 border-b border-white/10">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--neon-cyan)] to-[var(--neon-green)] flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-black" />
                            </div>
                            <div>
                                <h1 className="text-lg font-bold text-white tracking-wide">Choose Your Avatar</h1>
                                <p className="text-[10px] text-gray-500 tracking-widest">{AVATARS.length} PREMIUM OPTIONS</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-red-500/20 hover:border-red-500/50 transition-all group"
                        >
                            <X className="w-5 h-5 text-gray-400 group-hover:text-red-400 transition-colors" />
                        </button>
                    </header>

                    {/* Search */}
                    <div className="p-4 pb-2">
                        <div className="relative">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search avatars..."
                                className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:border-[var(--neon-cyan)]/50 focus:outline-none transition-colors"
                            />
                        </div>
                    </div>

                    {/* Categories */}
                    <div className="px-4 py-2 overflow-x-auto scrollbar-none">
                        <div className="flex gap-2">
                            {AVATAR_CATEGORIES.map((cat) => (
                                <button
                                    key={cat.id}
                                    onClick={() => setSelectedCategory(cat.id)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${selectedCategory === cat.id
                                            ? 'bg-[var(--neon-cyan)] text-black shadow-[0_0_20px_rgba(0,240,255,0.3)]'
                                            : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                                        }`}
                                >
                                    <span>{cat.icon}</span>
                                    <span>{cat.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Avatar Grid */}
                    <div className="flex-1 overflow-y-auto p-4">
                        <motion.div
                            layout
                            className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-3"
                        >
                            <AnimatePresence mode="popLayout">
                                {filteredAvatars.map((avatar, index) => (
                                    <motion.button
                                        key={avatar.id}
                                        layout
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.8 }}
                                        transition={{ delay: index * 0.02 }}
                                        onClick={() => handleSelect(avatar)}
                                        onMouseEnter={() => setHoveredAvatar(avatar.id)}
                                        onMouseLeave={() => setHoveredAvatar(null)}
                                        className={`relative aspect-square rounded-2xl overflow-hidden group transition-all duration-300 ${currentAvatar === avatar.url
                                                ? 'ring-2 ring-[var(--neon-cyan)] ring-offset-2 ring-offset-black shadow-[0_0_20px_rgba(0,240,255,0.4)]'
                                                : 'hover:ring-2 hover:ring-white/30 hover:ring-offset-2 hover:ring-offset-black'
                                            }`}
                                    >
                                        {/* Avatar Image */}
                                        <img
                                            src={avatar.url}
                                            alt={avatar.name}
                                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                                            loading="lazy"
                                        />

                                        {/* Hover Overlay */}
                                        <div className={`absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex items-end justify-center p-2 transition-opacity ${hoveredAvatar === avatar.id ? 'opacity-100' : 'opacity-0'
                                            }`}>
                                            <span className="text-[10px] text-white font-medium text-center truncate w-full">
                                                {avatar.name}
                                            </span>
                                        </div>

                                        {/* Selected Check */}
                                        {currentAvatar === avatar.url && (
                                            <div className="absolute top-1 right-1 w-6 h-6 bg-[var(--neon-cyan)] rounded-full flex items-center justify-center shadow-lg">
                                                <Check className="w-4 h-4 text-black" />
                                            </div>
                                        )}

                                        {/* Category Badge */}
                                        <div className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider ${avatar.category === 'professional' ? 'bg-blue-500/80 text-white' :
                                                avatar.category === 'casual' ? 'bg-green-500/80 text-white' :
                                                    avatar.category === 'abstract' ? 'bg-purple-500/80 text-white' :
                                                        avatar.category === 'animals' ? 'bg-orange-500/80 text-white' :
                                                            'bg-red-500/80 text-white'
                                            }`}>
                                            {avatar.category.slice(0, 3)}
                                        </div>
                                    </motion.button>
                                ))}
                            </AnimatePresence>
                        </motion.div>

                        {filteredAvatars.length === 0 && (
                            <div className="text-center py-20">
                                <div className="text-6xl mb-4">🔍</div>
                                <h3 className="text-gray-500 font-medium">No avatars found</h3>
                                <p className="text-gray-600 text-sm">Try a different search or category</p>
                            </div>
                        )}
                    </div>

                    {/* Preview Footer */}
                    <div className="p-4 border-t border-white/10 bg-black/50">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-white/5 overflow-hidden border border-white/10">
                                    {hoveredAvatar && (
                                        <img
                                            src={filteredAvatars.find(a => a.id === hoveredAvatar)?.url}
                                            className="w-full h-full object-cover"
                                            alt="Preview"
                                        />
                                    )}
                                </div>
                                <div className="text-sm">
                                    <span className="text-gray-500">Preview: </span>
                                    <span className="text-white font-medium">
                                        {hoveredAvatar ? filteredAvatars.find(a => a.id === hoveredAvatar)?.name : 'Hover to preview'}
                                    </span>
                                </div>
                            </div>
                            <div className="text-sm text-gray-500">
                                {filteredAvatars.length} avatars
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default AvatarLibrary;
